// Keyboard row-navigation singleton (Phase 5 Step 7 — mid-layer extraction, dependency-injected).
// Imports only leaves (ui, state). Its two app back-calls (fetchTickerData, prefetchTickerHistory) are
// INJECTED via configure() at boot — no import cycle, and the 560-line fetchTickerData hub stays in app.js.
// Object moved verbatim (8-space indent preserved; esbuild strips it on minify).
import { ui } from './ui.js';
import { state } from './state.js';

let _deps = {};
export function configure(d) { _deps = { ..._deps, ...d }; }

        export const RowNav = {
            ticker: null,           // selected symbol — survives re-renders so we can re-find the row
            loadTimer: null,
            LOAD_DELAY: 120,        // ms of quiet before the highlighted stock loads (prefetch keeps it instant)
            // Which table is on screen right now (null for tabs without ticker rows, e.g. Groups).
            container() {
                const tab = (typeof state.activeHubTab !== 'undefined') ? state.activeHubTab : 'screener';
                const id = tab === 'watchlist' ? 'watchlistTab'
                         : tab === 'alerts'    ? 'alertsTab'
                         : tab === 'portfolio' ? 'portfolioTab'
                         : tab === 'screener'  ? 'screenerResultsBody'
                         : null;
                if (!id) return null;
                const el = document.getElementById(id);
                if (el) this._observe(el);
                return el;
            },
            rows() {
                const c = this.container();
                return c ? Array.from(c.querySelectorAll('tr[data-ticker]')) : [];
            },
            currentIndex(rows) {
                if (this.ticker) {
                    const i = rows.findIndex(r => r.dataset.ticker === this.ticker);
                    if (i >= 0) return i;
                }
                return rows.findIndex(r => r.classList.contains('kbd-active-row'));
            },
            highlight(rows, idx) {
                rows.forEach(r => r.classList.remove('kbd-active-row'));
                const row = rows[idx];
                if (!row) return;
                row.classList.add('kbd-active-row');
                this.ticker = row.dataset.ticker;
                row.scrollIntoView({ block: 'nearest' });
            },
            move(delta) {
                const rows = this.rows();
                if (!rows.length) return false;
                let idx = this.currentIndex(rows);
                idx = idx < 0 ? (delta > 0 ? 0 : rows.length - 1)
                              : Math.min(rows.length - 1, Math.max(0, idx + delta));
                this.highlight(rows, idx);
                this.prefetchAhead(rows, idx, delta > 0 ? 1 : -1);
                this.scheduleLoad();
                return true;
            },
            // Warm the history cache for the next few rows in the direction of travel so
            // arriving on them is instant (no network). Best-effort & deduped inside
            // prefetchTickerHistory; never blocks. This is what makes scrubbing through
            // 100 charts feel spotless.
            prefetchAhead(rows, idx, dir) {
                if (typeof _deps.prefetchTickerHistory !== 'function') return;
                for (let k = 1; k <= 3; k++) {
                    const r = rows[idx + dir * k];
                    if (r && r.dataset.ticker) _deps.prefetchTickerHistory(r.dataset.ticker);
                }
                const back = rows[idx - dir]; // also warm the one behind, in case they reverse
                if (back && back.dataset.ticker) _deps.prefetchTickerHistory(back.dataset.ticker);
            },
            scheduleLoad() {
                clearTimeout(this.loadTimer);
                this.loadTimer = setTimeout(() => this.loadNow(), this.LOAD_DELAY);
            },
            loadNow() {
                clearTimeout(this.loadTimer);
                if (!this.ticker || !ui.tickerInput) return;
                if (ui.tickerInput.value !== this.ticker) ui.tickerInput.value = this.ticker;
                if (typeof _deps.fetchTickerData === 'function') _deps.fetchTickerData();
            },
            // Re-paint the highlight after a table re-renders (live prices, refresh, re-sort).
            _observe(container) {
                if (!container || container.__rowNavObserved) return;
                container.__rowNavObserved = true;
                const mo = new MutationObserver(() => {
                    if (!this.ticker) return;
                    const rows = container.querySelectorAll('tr[data-ticker]');
                    let already = false, match = null;
                    rows.forEach(r => {
                        if (r.classList.contains('kbd-active-row')) already = true;
                        if (r.dataset.ticker === this.ticker) match = r;
                    });
                    if (!already && match) match.classList.add('kbd-active-row');
                });
                mo.observe(container, { childList: true, subtree: true });
            }
        };

if (typeof window !== 'undefined') {
    window.RowNav = RowNav;                          // preserve the existing singleton global
    window.rowNav = { configure, deps: () => _deps }; // module DI/verification surface
}
